import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CaminoMapa, Mapa, MuelleMapa, MuroMapa, NivelPosicion, Sector } from '../api';
import { AnimatePresence, motion } from 'motion/react';
import { Icono, Numero } from './ui';
import { desplegar, pop, resorte, suave } from './movimiento';
import { useEsCelular } from '../lib/useMedia';
import { botonSecundario, c, COLOR_MURO, colorAbertura, colorCamino, colorSector, mono, tarjeta } from '../theme';
import { HOLGURA_M, hayDintel, llegaAlPiso } from '../tres/geometria';
import {
  ALTURA,
  CAMARA_INICIAL,
  ZOOM,
  acercar as acercarVista,
  acotar,
  baseDelTramo,
  carasVisibles,
  enElPiso,
  encuadrarCaja,
  esquinas,
  envolver,
  hacerCamara,
  orbitar,
  poli,
  segunLaPantalla,
  type Base,
  type Camara,
  type Punto,
  type Punto3,
  type Rect,
} from '../lib/camara3d';

/**
 * Vista 3D del depósito, en proyección axonométrica.
 *
 * Dibuja la misma planta que el editor, pero levantando los niveles: cada
 * hueco de rack es una caja apoyada sobre la huella real de su posición, a la
 * altura que le corresponde. No hay modelo aparte ni medidas inventadas, es la
 * geometría que ya viene en el mapa.
 *
 * La cámara tiene dos ángulos libres —giro alrededor del depósito y altura del
 * punto de vista— más zoom y desplazamiento. Se dibuja en SVG con metros como
 * unidad, así que acercarse no pierde definición: el dibujo se rehace.
 */

/**
 * Prisma sobre una huella: las caras laterales que dan a la cámara, y encima
 * el techo, que mirando desde arriba se ve siempre.
 *
 * `adentro` es para los muros: un punto del lado que se quiere mirar (el medio
 * de la nave), y hace que la cara de afuera del muro no se dibuje. Ver
 * `carasVisibles`.
 */
function Caja({
  base,
  z0,
  z1,
  camara,
  relleno,
  borde,
  grosorBorde = 0.02,
  opacidad,
  adentro,
}: {
  base: Base;
  z0: number;
  z1: number;
  camara: Camara;
  relleno: string;
  borde: string;
  grosorBorde?: number;
  opacidad: number;
  adentro?: Punto3 | null;
}) {
  return (
    <g opacity={opacidad}>
      {carasVisibles(base, camara, z0, z1, adentro ?? undefined).map((cara, i) => (
        <polygon key={i} points={poli(cara)} fill={relleno} stroke={borde} strokeWidth={grosorBorde} opacity={0.85} />
      ))}
      <polygon
        points={poli(base.map((p) => camara.proyectar(p.x, p.y, z1)))}
        fill={relleno}
        stroke={borde}
        strokeWidth={grosorBorde}
        opacity={0.58}
      />
    </g>
  );
}

/**
 * Un ángulo de cámara con su deslizador. Va a nivel de módulo y no adentro de
 * la vista: definido adentro sería un componente distinto en cada render, y el
 * deslizador se remontaría justo mientras uno lo está arrastrando.
 */
function Angulo({
  rotulo,
  valor,
  min,
  max,
  ancho,
  onCambiar,
}: {
  rotulo: string;
  valor: number;
  min: number;
  max: number;
  /** En el celular ocupa el renglón entero; en escritorio, lo justo. */
  ancho: 'entero' | 'justo';
  onCambiar: (v: number) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12.5,
        color: c.texto2,
        flex: ancho === 'entero' ? '1 1 100%' : undefined,
        minWidth: 0,
      }}
    >
      {rotulo}
      <input
        type="range"
        min={min}
        max={max}
        value={Math.round(valor)}
        onChange={(e) => onCambiar(Number(e.target.value))}
        aria-label={rotulo === 'Giro' ? 'Girar la cámara alrededor del depósito' : 'Altura del punto de vista'}
        style={{
          flex: 1,
          minWidth: 60,
          width: ancho === 'entero' ? undefined : 112,
          accentColor: c.verde,
          cursor: 'pointer',
        }}
      />
      <span style={{ fontFamily: mono, fontSize: 11.5, color: c.texto3, width: 30 }}>{Math.round(valor)}°</span>
    </label>
  );
}


// Con qué se pinta cada tipo de abertura: una sola tabla para toda la app,
// en `theme.ts` (`COLOR_ABERTURA`/`colorAbertura`), junto a `COLOR_CAMINO`.

/**
 * ¿Es un muro del perímetro de su nave? Se lo pregunta a la geometría -las dos
 * puntas caen sobre el mismo borde de la caja de la nave- y no a `compartido`,
 * que habla de otra cosa (si dos naves se reparten el mismo plano). Hace falta
 * porque un perimetral y una pared interior no se pueden dibujar igual: del
 * perimetral hay que sacarle la cara de afuera para poder mirar el interior,
 * y una pared interior tiene las dos caras para adentro.
 *
 * Sirve igual con los muros recortados que manda `/mapa` (los que se reparten
 * un plano con la nave de al lado, con clave `n2-oeste#1`): el recorte es un
 * pedazo del mismo borde, así que sigue cayendo sobre él.
 */
function esPerimetral(m: MuroMapa, caja: Rect): boolean {
  const pegadoEnX = (x: number) => Math.abs(m.a.x - x) <= HOLGURA_M && Math.abs(m.b.x - x) <= HOLGURA_M;
  const pegadoEnY = (y: number) => Math.abs(m.a.y - y) <= HOLGURA_M && Math.abs(m.b.y - y) <= HOLGURA_M;
  return pegadoEnX(caja.x) || pegadoEnX(caja.x + caja.ancho) || pegadoEnY(caja.y) || pegadoEnY(caja.y + caja.alto);
}

/** Un pedazo de muro listo para dibujar: pared, hueco de abertura o dintel. */
type PiezaMuro = {
  clave: string;
  base: Base;
  /** El medio del pedazo: con eso se lo ordena por profundidad. */
  centro: Punto3;
  z0: number;
  z1: number;
  relleno: string;
  borde: string;
  opacidad: number;
  /** El globito. Sólo las aberturas llevan; la pared no dice nada. */
  rotulo: string | null;
  /** Hacia dónde queda el interior de la nave, si el muro es perimetral. */
  adentro: Punto3 | null;
};

/**
 * En qué pedazos se dibuja un muro: los tramos macizos -que ya vienen del
 * backend con los huecos descontados-, el hueco de cada abertura a su altura
 * real, y el **dintel**: la pared que queda arriba de una abertura que llega
 * al piso pero no al techo.
 *
 * El dintel no es un lujo: un portón de 4,5 m en un muro de 8 hace que
 * `tramosMacizos` parta el muro en dos tramos que van del piso al techo, así
 * que entre el 4,5 y el 8 no queda nada dibujado y el galpón tiene una ranura
 * de 4 × 3,5 m que lo atraviesa hasta el techo. Un ventanal con antepecho es
 * el caso contrario y no necesita caja propia: como no llega al piso,
 * `tramosMacizos` no parte el muro, y el tramo entero -del piso al techo- ya
 * dibuja la pared de abajo y la de arriba; el hueco se pinta encima, a su
 * altura, y se lee como un ventanal alto. El antepecho, entonces, no lleva
 * caja; el dintel sí.
 */
function piezasDelMuro(m: MuroMapa, adentro: Punto3 | null): PiezaMuro[] {
  const medio = (a: Punto3, b: Punto3): Punto3 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const piezas: PiezaMuro[] = m.tramos.map((t, i) => ({
    clave: `${m.clave}-t${i}`,
    base: baseDelTramo(t.a, t.b, m.espesor_m),
    centro: medio(t.a, t.b),
    z0: 0,
    z1: m.alto_m,
    relleno: COLOR_MURO.relleno,
    borde: COLOR_MURO.borde,
    opacidad: 0.95,
    rotulo: null,
    adentro,
  }));

  for (const ab of m.aberturas) {
    const base = baseDelTramo(ab.a, ab.b, m.espesor_m);
    const centro = medio(ab.a, ab.b);
    const color = colorAbertura(ab.tipo);
    const arriba = ab.antepecho_m + ab.alto_m;

    piezas.push({
      clave: `${m.clave}-a${ab.id}`,
      base,
      centro,
      z0: ab.antepecho_m,
      z1: arriba,
      relleno: color,
      borde: color,
      opacidad: 0.55,
      rotulo: `${ab.nombre || ab.tipo} · alto ${ab.alto_m} m`,
      adentro,
    });

    // El dintel va con el color de la pared, porque es pared.
    if (llegaAlPiso(ab) && hayDintel(ab, m.alto_m)) {
      piezas.push({
        clave: `${m.clave}-d${ab.id}`,
        base,
        centro,
        z0: arriba,
        z1: m.alto_m,
        relleno: COLOR_MURO.relleno,
        borde: COLOR_MURO.borde,
        opacidad: 0.95,
        rotulo: null,
        adentro,
      });
    }
  }

  return piezas;
}

/**
 * Un pedazo de muro dibujado, con su globito si es una abertura.
 *
 * El globito vive en un `<g>` que **sí** recibe el puntero: metido dentro de
 * un `pointer-events: none`, como estaba, el navegador no lo dispara nunca y
 * el título era decorado muerto (en el plano 2D el mismo título sí anda). No
 * le quita el toque a ningún rack: los racks se dibujan después y en SVG el
 * que está encima es el que agarra el puntero.
 */
function PedazoDeMuro({ pieza, camara }: { pieza: PiezaMuro; camara: Camara }) {
  const caja = (
    <Caja
      base={pieza.base}
      z0={pieza.z0}
      z1={pieza.z1}
      camara={camara}
      relleno={pieza.relleno}
      borde={pieza.borde}
      grosorBorde={0.02}
      opacidad={pieza.opacidad}
      adentro={pieza.adentro}
    />
  );
  if (!pieza.rotulo) return caja;
  return (
    <g>
      <title>{pieza.rotulo}</title>
      {caja}
    </g>
  );
}

type Hueco = {
  clave: string;
  codigo: string;
  sector: string;
  rack: string;
  base: Base;
  centro: Punto3;
  z0: number;
  z1: number;
  nivel: NivelPosicion;
  hit: boolean;
};

/** Lo que se muestra en el panel flotante al tocar un hueco. */
type Elegido = {
  sector: Sector;
  rack: Sector['racks'][number];
  posicion: Sector['racks'][number]['posiciones'][number];
};

export default function Isometrico({
  predio,
  naves,
  sectores,
  muelles,
  caminos,
  seleccion,
  onPosicion,
}: {
  predio: Mapa['predio'];
  naves: Mapa['naves'];
  sectores: Sector[];
  muelles: MuelleMapa[];
  caminos: CaminoMapa[];
  seleccion: string | null;
  onPosicion: (codigo: string) => void;
}) {
  const [giro, setGiro] = useState(CAMARA_INICIAL.giro);
  const [altura, setAltura] = useState(CAMARA_INICIAL.altura);
  const [zoom, setZoom] = useState(1);
  const [centro, setCentro] = useState<{ x: number; y: number } | null>(null);
  const [foco, setFoco] = useState<string | null>(null);
  const [elegido, setElegido] = useState<string | null>(seleccion);
  const esCelular = useEsCelular();
  // En el celular la lista de sectores tapa medio dibujo: arranca cerrada.
  const [verSectores, setVerSectores] = useState(!esCelular);
  const [moviendo, setMoviendo] = useState(false);

  // Aislar un rack: mirándolo derecho, todo lo que esté delante lo tapa, así
  // que mientras se lo mira de cerca el resto no se dibuja.
  const [aislado, setAislado] = useState<string | null>(null);
  // Forma del recuadro donde se dibuja, para que el encuadre no deje bandas
  // vacías al costado.
  const [relacion, setRelacion] = useState(1.6);

  const svg = useRef<SVGSVGElement>(null);
  // Punteros activos: uno arrastra, dos hacen pinza.
  const punteros = useRef(new Map<number, { x: number; y: number }>());
  const arrastre = useRef<{ px: number; py: number; cx: number; cy: number; u: number; movio: boolean } | null>(null);
  const pinza = useRef<{ distancia: number; zoom: number } | null>(null);
  const orbita = useRef<{
    px: number;
    py: number;
    giro: number;
    altura: number;
    /** Punto del piso que se mantiene quieto mientras se gira. */
    pivote: Punto3;
  } | null>(null);

  useLayoutEffect(() => {
    const el = svg.current;
    if (!el) return;
    const medir = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setRelacion(r.width / r.height);
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  // La rueda sobre el dibujo acerca, no mueve la página. Va como oyente
  // nativo porque React registra `wheel` en modo pasivo y ahí preventDefault
  // no tiene efecto.
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const alGirarRueda = (e: WheelEvent) => e.preventDefault();
    el.addEventListener('wheel', alGirarRueda, { passive: false });
    return () => el.removeEventListener('wheel', alGirarRueda);
  }, []);

  const camara = useMemo(() => hacerCamara(giro, altura), [giro, altura]);
  /** El color que tiene guardado el sector, o uno por su orden si no tiene. */
  const colorDe = (id: string) => {
    const i = sectores.findIndex((s) => s.id === id);
    return colorSector(sectores[i]?.color, Math.max(i, 0));
  };

  const huecos = useMemo(() => {
    const lista: Hueco[] = [];
    for (const s of sectores) {
      for (const r of s.racks) {
        for (const p of r.posiciones) {
          if (!p.caja) continue;
          const base = esquinas(p.caja);
          const medio = { x: p.caja.x + p.caja.ancho / 2, y: p.caja.y + p.caja.alto / 2 };
          for (const n of p.niveles) {
            const alto = r.geometria.altoNivelM;
            lista.push({
              clave: `${p.codigo}-N${n.nivel}`,
              codigo: p.codigo,
              sector: s.id,
              rack: r.id,
              base,
              centro: medio,
              z0: (n.nivel - 1) * alto,
              z1: n.nivel * alto - 0.06, // una luz entre niveles, para leer la pila
              nivel: n,
              hit: p.hit,
            });
          }
        }
      }
    }
    return lista;
  }, [sectores]);

  /**
   * Los muros en pedazos, repartidos según cómo hay que dibujarlos:
   *
   * - los **perimetrales** van en una capa propia, antes de todo lo que está
   *   apoyado en el piso, y con la cara de afuera descartada (`adentro`). Así
   *   el muro que queda entre la cámara y el interior desaparece -el galpón se
   *   abre- y de los lejanos se ve la cara de adentro, que está detrás de
   *   cualquier cosa que esté dentro de la nave: mirando de afuera, el rayo de
   *   la vista atraviesa el interior antes de llegar a esa pared. Dibujarlos
   *   primero, entonces, no es una aproximación: es el orden correcto.
   * - las **paredes interiores** se ordenan por profundidad junto con los
   *   huecos de rack, porque una pared interior sí puede quedar delante de un
   *   rack y taparlo, y ahí el orden importa.
   */
  const muros = useMemo(() => {
    const perimetrales: PiezaMuro[] = [];
    const interiores: PiezaMuro[] = [];
    for (const n of naves) {
      const medio = { x: n.caja.x + n.caja.ancho / 2, y: n.caja.y + n.caja.alto / 2 };
      for (const m of n.muros) {
        const adentro = esPerimetral(m, n.caja) ? medio : null;
        (adentro ? perimetrales : interiores).push(...piezasDelMuro(m, adentro));
      }
    }
    return { perimetrales, interiores };
  }, [naves]);

  // Algoritmo del pintor: lo más lejano se dibuja primero. El orden depende
  // del giro, así que se rehace cada vez que se mueve la cámara.
  const enOrden = useMemo(() => {
    const piezas = [
      ...huecos.map((h) => ({ que: 'hueco' as const, clave: h.clave, centro: h.centro, z0: h.z0, hueco: h })),
      ...muros.interiores.map((p) => ({ que: 'pared' as const, clave: p.clave, centro: p.centro, z0: p.z0, pared: p })),
    ];
    return piezas.sort(
      (a, b) => camara.hacia(a.centro.x, a.centro.y) - camara.hacia(b.centro.x, b.centro.y) || a.z0 - b.z0,
    );
  }, [huecos, muros, camara]);

  const detalle: Elegido | null = useMemo(() => {
    if (!elegido) return null;
    for (const s of sectores) {
      for (const r of s.racks) {
        const p = r.posiciones.find((q) => q.codigo === elegido);
        if (p) return { sector: s, rack: r, posicion: p };
      }
    }
    return null;
  }, [elegido, sectores]);

  const altoM = Math.max(...naves.map((n) => n.alto_m), ...huecos.map((h) => h.z1), 1);

  // La caja del predio entero: el encuadre y el pivote del giro se calculan
  // sobre ella, no sobre una nave sola, porque son dos y están pegadas.
  const cajaDelPredio = { x: 0, y: 0, ancho: predio.ancho, alto: predio.largo };

  // Encuadre completo: los ocho vértices del predio, proyectados. Cambia con
  // el giro, porque el depósito se ve más ancho o más angosto.
  const encuadre = useMemo(
    () => segunLaPantalla(encuadrarCaja(camara, cajaDelPredio, altoM), relacion),
    [camara, cajaDelPredio.ancho, cajaDelPredio.alto, altoM, relacion],
  );

  const cx = centro?.x ?? encuadre.x + encuadre.ancho / 2;
  const cy = centro?.y ?? encuadre.y + encuadre.alto / 2;
  const vistaAncho = encuadre.ancho / zoom;
  const vistaAlto = encuadre.alto / zoom;
  const viewBox = `${cx - vistaAncho / 2} ${cy - vistaAlto / 2} ${vistaAncho} ${vistaAlto}`;

  /**
   * Cuántas unidades del dibujo entra un píxel, sacado de la matriz real del
   * SVG. No sirve dividir por el ancho del elemento: con `meet` el dibujo
   * puede quedar limitado por el alto y ahí la cuenta da distinto.
   */
  function porPixel() {
    const m = svg.current?.getScreenCTM();
    return m && m.a !== 0 ? 1 / m.a : 1;
  }

  /** Un punto de pantalla, en las unidades del dibujo. */
  function aUnidades(e: { clientX: number; clientY: number }) {
    const el = svg.current;
    if (!el) return null;
    const p = el.createSVGPoint();
    p.x = e.clientX;
    p.y = e.clientY;
    const m = el.getScreenCTM();
    if (!m) return null;
    const q = p.matrixTransform(m.inverse());
    return { x: q.x, y: q.y };
  }

  function acercar(factor: number, ancla?: { x: number; y: number }) {
    const v = acercarVista({ cx, cy, zoom }, factor, ancla);
    if (v.zoom === zoom) return;
    setCentro({ x: v.cx, y: v.cy });
    setZoom(v.zoom);
  }

  /**
   * Deja el rack de frente y ocupando la pantalla: derecho —giro y altura en
   * cero, como mirar la estantería parado enfrente— y sin el resto del
   * depósito, que desde ese ángulo se le cruza por delante.
   */
  function centrarRack(rack: Sector['racks'][number]) {
    const cam = hacerCamara(0, 0);
    const alto = rack.niveles * rack.geometria.altoNivelM;
    const puntos: Punto[] = [];
    for (const p of rack.posiciones) {
      if (!p.caja) continue;
      for (const e of esquinas(p.caja)) for (const z of [0, alto]) puntos.push(cam.proyectar(e.x, e.y, z));
    }
    const caja = envolver(puntos, 0.8);
    if (!caja) return;

    const base = segunLaPantalla(encuadrarCaja(cam, cajaDelPredio, altoM), relacion);
    const objetivo = segunLaPantalla(caja, relacion);

    setGiro(0);
    setAltura(0);
    setZoom(acotar(base.ancho / objetivo.ancho, ZOOM.min, ZOOM.max));
    setCentro({ x: objetivo.x + objetivo.ancho / 2, y: objetivo.y + objetivo.alto / 2 });
    setAislado(rack.id);
  }

  /** Vuelve al predio completo. */
  function verTodo() {
    setZoom(1);
    setCentro(null);
    setGiro(CAMARA_INICIAL.giro);
    setAltura(CAMARA_INICIAL.altura);
    setAislado(null);
    setFoco(null);
  }

  function alBajar(e: React.PointerEvent<SVGSVGElement>) {
    (e.target as Element).setPointerCapture?.(e.pointerId);

    // Con la ruedita apretada se da la vuelta al depósito: a lo ancho gira y
    // a lo alto sube o baja el punto de vista. Se gira alrededor de lo que se
    // está mirando, no del rincón (0,0) del predio, que es lo que hacía que el
    // depósito entero se corriera de lugar al girar.
    if (e.button === 1) {
      // Sin encuadre propio se gira alrededor del medio del predio, que es lo
      // que uno espera; ya habiendo movido la vista, alrededor de lo que se
      // esté mirando.
      const pivote = (centro && camara.dePiso(cx, cy)) ?? {
        x: predio.ancho / 2,
        y: predio.largo / 2,
      };
      orbita.current = { px: e.clientX, py: e.clientY, giro, altura, pivote };
      setMoviendo(true);
      return;
    }

    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()];
      pinza.current = { distancia: Math.hypot(a!.x - b!.x, a!.y - b!.y), zoom };
      arrastre.current = null;
      return;
    }
    // La escala se congela al empezar: durante el arrastre el zoom no cambia.
    arrastre.current = { px: e.clientX, py: e.clientY, cx, cy, u: porPixel(), movio: false };
  }

  function alMover(e: React.PointerEvent<SVGSVGElement>) {
    const o = orbita.current;
    if (o) {
      const angulos = orbitar(o, e.clientX - o.px, e.clientY - o.py);
      setGiro(angulos.giro);
      setAltura(angulos.altura);
      // Se recoloca la cámara para que el pivote siga donde estaba.
      const nueva = hacerCamara(angulos.giro, angulos.altura);
      const en = nueva.proyectar(o.pivote.x, o.pivote.y, 0);
      setCentro({ x: en.sx, y: en.sy });
      return;
    }

    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (punteros.current.size === 2 && pinza.current) {
      const [a, b] = [...punteros.current.values()];
      const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      setZoom(acotar((pinza.current.zoom * d) / pinza.current.distancia, ZOOM.min, ZOOM.max));
      return;
    }

    const a = arrastre.current;
    if (!a) return;
    const dxPx = e.clientX - a.px;
    const dyPx = e.clientY - a.py;
    if (!a.movio && Math.hypot(dxPx, dyPx) > 4) {
      a.movio = true;
      setMoviendo(true);
    }
    if (!a.movio) return;
    setCentro({ x: a.cx - dxPx * a.u, y: a.cy - dyPx * a.u });
  }

  function alSoltar(e: React.PointerEvent<SVGSVGElement>) {
    if (orbita.current) {
      orbita.current = null;
      setMoviendo(false);
      return;
    }

    punteros.current.delete(e.pointerId);
    if (punteros.current.size < 2) pinza.current = null;

    // Si se levanta un dedo de la pinza y queda el otro apoyado, el arrastre
    // tiene que empezar de nuevo: el que había quedado guardaba la escala y el
    // centro de antes del zoom, y el dibujo pegaba un salto.
    if (punteros.current.size === 1) {
      const [queda] = [...punteros.current.values()];
      arrastre.current = { px: queda!.x, py: queda!.y, cx, cy, u: porPixel(), movio: false };
      return;
    }

    if (punteros.current.size === 0) {
      // El flag de movimiento se limpia después, para que el toque que viene
      // atrás del arrastre no se lea como un click sobre el hueco.
      setMoviendo(false);
      setTimeout(() => {
        arrastre.current = null;
      }, 0);
    }
  }

  /** Un toque limpio elige el hueco; si venía moviendo la cámara, no. */
  function alTocar(codigo: string) {
    if (orbita.current || arrastre.current?.movio) return;
    setElegido(codigo);
  }

  const ocupados = huecos.filter((h) => h.nivel.ocupado).length;
  const conArea = sectores.filter((s) => s.area !== null);

  const botonBarra: React.CSSProperties = {
    height: 36,
    padding: esCelular ? 0 : '0 12px',
    width: esCelular ? 36 : undefined,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 999,
    border: `1px solid ${c.bordeFuerte}`,
    background: c.panel,
    color: c.texto2,
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
  };

  const controles = [
    { t: 'Acercar', i: 'add', f: () => acercar(1.3) },
    { t: 'Alejar', i: 'remove', f: () => acercar(1 / 1.3) },
    { t: 'Encuadrar', i: 'fit_screen', f: verTodo },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <Angulo
          rotulo="Giro"
          valor={giro}
          min={0}
          max={359}
          ancho={esCelular ? 'entero' : 'justo'}
          onCambiar={setGiro}
        />
        <Angulo
          rotulo="Altura"
          valor={altura}
          min={ALTURA.min}
          max={ALTURA.max}
          ancho={esCelular ? 'entero' : 'justo'}
          onCambiar={setAltura}
        />

        {/* En el celular el zoom va flotando sobre el dibujo, como en un mapa:
            la barra ya se lleva dos renglones con los dos ángulos. */}
        {!esCelular &&
          controles.map((b) => (
            <button key={b.t} type="button" onClick={b.f} aria-label={b.t} title={b.t} style={botonBarra}>
              <Icono nombre={b.i} size={17} color={c.texto3} />
              {b.t}
            </button>
          ))}

        <span style={{ fontSize: 12.5, color: c.texto3 }}>
          {/* El zoom no pasa por `Numero`: cambia con cada ruedita y quedaría
              corriendo detrás de la mano. */}
          <Numero valor={ocupados} />
          {esCelular
            ? `/${huecos.length} huecos · ${Math.round(zoom * 100)}%`
            : ` de ${huecos.length} huecos con pallet · ${Math.round(zoom * 100)}%`}
        </span>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 'min(70vh, 600px)',
          background: c.fondo,
          border: `1px solid ${c.borde}`,
          borderRadius: 12,
          overflow: 'hidden',
          overscrollBehavior: 'contain',
        }}
      >
        <svg
          ref={svg}
          viewBox={viewBox}
          style={{
            width: '100%',
            display: 'block',
            height: '100%',
            touchAction: 'none',
            cursor: orbita.current ? 'move' : moviendo ? 'grabbing' : 'grab',
          }}
          role="img"
          aria-label="Vista tridimensional del depósito"
          onMouseDown={(e) => {
            // Sin esto, el botón del medio abre el desplazamiento automático
            // del navegador y el gesto de giro se pierde.
            if (e.button === 1) e.preventDefault();
          }}
          onPointerDown={alBajar}
          onPointerMove={alMover}
          onPointerUp={alSoltar}
          onPointerCancel={alSoltar}
          onWheel={(e) => {
            const p = aUnidades(e);
            acercar(e.deltaY < 0 ? 1.12 : 1 / 1.12, p ?? undefined);
          }}
        >
          {naves.map((n) => (
            <g key={n.id}>
              <polygon points={enElPiso(camara, n.caja)} fill="#fff" stroke={c.bordeFuerte} strokeWidth={0.06} />
              {/* Contorno de la nave levantado hasta su alto: es la nave, no el
                  predio, la que tiene altura, y son dos distintas. */}
              {esquinas(n.caja).map((p, i) => {
                const a = camara.proyectar(p.x, p.y, 0);
                const b = camara.proyectar(p.x, p.y, n.alto_m);
                return <line key={i} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy} stroke={c.borde} strokeWidth={0.05} />;
              })}
              {esquinas(n.caja).map((p, i, todas) => {
                const q = todas[(i + 1) % 4]!;
                const a = camara.proyectar(p.x, p.y, n.alto_m);
                const b = camara.proyectar(q.x, q.y, n.alto_m);
                return <line key={`t${i}`} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy} stroke={c.borde} strokeWidth={0.05} />;
              })}
            </g>
          ))}

          {/* Grilla de cinco metros, para dar escala. Cada nave la recorre
              desde su propio rincón: una grilla global no coincidiría con los
              bordes de la segunda nave, que no arranca en (0,0). */}
          {naves.flatMap((n) =>
            Array.from({ length: Math.floor((n.caja.ancho - 0.01) / 5) }, (_, i) => (i + 1) * 5).map((d) => {
              const a = camara.proyectar(n.caja.x + d, n.caja.y, 0);
              const b = camara.proyectar(n.caja.x + d, n.caja.y + n.caja.alto, 0);
              return <line key={`gx${n.id}-${d}`} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy} stroke={c.borde} strokeWidth={0.03} />;
            }),
          )}
          {naves.flatMap((n) =>
            Array.from({ length: Math.floor((n.caja.alto - 0.01) / 5) }, (_, i) => (i + 1) * 5).map((d) => {
              const a = camara.proyectar(n.caja.x, n.caja.y + d, 0);
              const b = camara.proyectar(n.caja.x + n.caja.ancho, n.caja.y + d, 0);
              return <line key={`gy${n.id}-${d}`} x1={a.sx} y1={a.sy} x2={b.sx} y2={b.sy} stroke={c.borde} strokeWidth={0.03} />;
            }),
          )}

          {/* Caminos y muelles: pintura sobre el piso, apenas por encima */}
          {caminos.map((k) => {
            const paleta = colorCamino(k.tipo);
            return (
              <g key={`k${k.id}`} style={{ pointerEvents: 'none' }}>
                <title>{k.nombre}</title>
                <polygon
                  points={enElPiso(camara, k.caja, 0.01)}
                  fill={paleta.relleno}
                  stroke={paleta.borde}
                  strokeWidth={0.04}
                />
              </g>
            );
          })}

          {muelles.map((m) =>
            m.caja ? (
              <g key={m.id} style={{ pointerEvents: 'none' }}>
                <title>{`Muelle ${m.id}`}</title>
                <polygon points={enElPiso(camara, m.caja, 0.02)} fill="#dfe4ea" stroke={c.bordeFuerte} strokeWidth={0.04} />
              </g>
            ) : null,
          )}

          {/*
            Muros del perímetro: su propia capa, arriba de los caminos y
            abajo de todo lo que está apoyado en el piso. De cada muro se
            dibuja sólo la cara de adentro -la de afuera es justamente la que
            taparía el interior-, y por eso esta capa puede ir antes de los
            racks sin dejarlos pintados encima de la pared: la cara de adentro
            de un muro está siempre detrás de lo que hay en la nave. Los
            tramos, los huecos de las aberturas y los dinteles los arma
            `piezasDelMuro`; el descarte de la cara de afuera, `carasVisibles`.

            Las paredes interiores no van acá: se ordenan por profundidad
            junto con los huecos de rack, más abajo.
          */}
          {muros.perimetrales.map((p) => (
            <PedazoDeMuro key={p.clave} pieza={p} camara={camara} />
          ))}

          {enOrden.map((p) => {
            if (p.que === 'pared') {
              // Aislado un rack se mira una sola estantería de frente: una
              // pared interior por delante es exactamente lo que hay que
              // sacar del medio, igual que los otros racks.
              if (aislado) return null;
              return <PedazoDeMuro key={p.clave} pieza={p.pared} camara={camara} />;
            }

            const h = p.hueco;
            // Aislado un rack, o puesto el foco en un sector, lo demás no se
            // dibuja: atenuado seguía tapando lo que se quería mirar.
            if (aislado ? h.rack !== aislado : foco !== null && foco !== h.sector) return null;

            const activo = elegido === h.codigo;
            const color = colorDe(h.sector);
            return (
              <g key={h.clave} onPointerUp={() => alTocar(h.codigo)} style={{ cursor: 'pointer' }}>
                <title>
                  {`${h.codigo} N${h.nivel.nivel} · ${
                    h.nivel.ocupado ? `${h.nivel.pallet} · ${h.nivel.articulo ?? ''}` : 'libre'
                  }`}
                </title>
                {/* El elegido se marca con el contorno, no con el relleno:
                    pintarlo de verde lo hacía pasar por hueco con pallet. */}
                <Caja
                  base={h.base}
                  z0={h.z0}
                  z1={h.z1}
                  camara={camara}
                  relleno={h.hit ? c.ambarSuave : h.nivel.ocupado ? color : 'rgba(255,255,255,.45)'}
                  borde={activo ? c.texto : h.hit ? c.ambar : h.nivel.ocupado ? color : c.bordeFuerte}
                  grosorBorde={activo ? 0.07 : 0.02}
                  opacidad={h.nivel.ocupado || h.hit || activo ? 0.95 : 0.5}
                />
              </g>
            );
          })}

          {huecos.length === 0 && (
            <text x={cx} y={cy} textAnchor="middle" fontSize={vistaAncho / 28} fill={c.texto3}>
              La planta todavía no está dibujada.
            </text>
          )}
        </svg>

        {esCelular && (
          <div style={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {controles.map((b) => (
              <button
                key={b.t}
                type="button"
                onClick={b.f}
                aria-label={b.t}
                title={b.t}
                style={{
                  height: 38,
                  width: 38,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 999,
                  border: `1px solid ${c.bordeFuerte}`,
                  background: 'rgba(255,255,255,.94)',
                  cursor: 'pointer',
                }}
              >
                <Icono nombre={b.i} size={19} color={c.texto2} />
              </button>
            ))}
          </div>
        )}

        {/* El centrado va por `x` de Motion: un `transform` escrito a mano lo
            pisaría la animación de entrada. */}
        <AnimatePresence>
        {aislado && (
          <motion.button
            key="aislado"
            {...pop}
            type="button"
            onClick={verTodo}
            style={{
              position: 'absolute',
              left: '50%',
              top: 10,
              x: '-50%',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 34,
              padding: '0 14px',
              borderRadius: 999,
              border: `1px solid ${c.verde}`,
              background: c.verdeSuave,
              color: c.verde,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(26,26,26,.12)',
            }}
          >
            <span style={{ fontFamily: mono }}>{aislado}</span>
            solo · ver todo
            <Icono nombre="close" size={15} color={c.verde} />
          </motion.button>
        )}
        </AnimatePresence>

        {/* ------------------------------------------- menú flotante: sectores */}
        {/* El ancho se anima junto con la lista: si saltara a `auto` de
            golpe, la lista se cerraría aplastada. */}
        <motion.div
          initial={false}
          animate={{ width: verSectores ? 210 : 'auto' }}
          transition={suave}
          style={{
            position: 'absolute',
            left: 10,
            top: 10,
            maxWidth: esCelular ? 'calc(100% - 76px)' : 'calc(100% - 24px)',
            ...tarjeta,
            padding: 10,
            boxShadow: '0 6px 20px rgba(26,26,26,.12)',
          }}
        >
          <button
            type="button"
            onClick={() => setVerSectores((v) => !v)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: 0,
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: c.texto3,
            }}
          >
            Sectores
            <span style={{ flex: 1 }} />
            <motion.span
              initial={false}
              animate={{ rotate: verSectores ? 180 : 0 }}
              transition={suave}
              style={{ display: 'grid', placeItems: 'center' }}
            >
              <Icono nombre="expand_more" size={17} color={c.texto3} />
            </motion.span>
          </button>

          <AnimatePresence initial={false}>
          {verSectores && (
            <motion.div key="lista" {...desplegar}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
              {conArea.length === 0 && (
                <span style={{ fontSize: 12, color: c.texto3 }}>Ningún sector está dibujado todavía.</span>
              )}
              {conArea.map((s) => {
                const activo = foco === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setFoco(activo ? null : s.id)}
                    title={s.uso}
                    style={{
                      position: 'relative',
                      isolation: 'isolate',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 7px',
                      borderRadius: 8,
                      border: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      background: 'transparent',
                    }}
                  >
                    {/* El resaltado del sector en foco viaja de uno a otro. */}
                    {activo && (
                      <motion.span
                        layoutId="iso-sector-foco"
                        transition={resorte}
                        aria-hidden="true"
                        style={{ position: 'absolute', inset: 0, borderRadius: 8, background: c.verdeSuave, zIndex: -1 }}
                      />
                    )}
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        flexShrink: 0,
                        background: colorDe(s.id),
                      }}
                    />
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: c.texto, minWidth: 0, flex: 1 }}>
                      {s.nombre}
                    </span>
                    <span style={{ fontSize: 11.5, color: c.texto3, fontVariantNumeric: 'tabular-nums' }}>
                      {s.pct}%
                    </span>
                  </button>
                );
              })}
              <AnimatePresence initial={false}>
              {foco && (
                <motion.span
                  key="foco"
                  {...desplegar}
                  style={{ ...desplegar.style, display: 'block', fontSize: 11.5, color: c.texto3, padding: '4px 7px', textWrap: 'pretty' }}
                >
                  Mostrando sólo {sectores.find((s) => s.id === foco)?.nombre}. Tocá de nuevo para ver todo.
                </motion.span>
              )}
              </AnimatePresence>
            </div>
            </motion.div>
          )}
          </AnimatePresence>
        </motion.div>

        {/* --------------------------------------- menú flotante: rack elegido */}
        {/* Crece desde su esquina de abajo a la derecha, que es donde vive. */}
        <AnimatePresence>
        {detalle && (
          <motion.div
            key="detalle"
            {...pop}
            style={{
              transformOrigin: 'bottom right',
              position: 'absolute',
              right: esCelular ? 10 : 12,
              bottom: esCelular ? 10 : 12,
              left: esCelular ? 10 : undefined,
              width: esCelular ? undefined : 258,
              maxWidth: 'calc(100% - 24px)',
              // En el celular el dibujo es chico: la ficha se queda con la
              // mitad de abajo y scrollea adentro en vez de salirse.
              maxHeight: esCelular ? '46%' : undefined,
              overflowY: esCelular ? 'auto' : undefined,
              ...tarjeta,
              padding: 12,
              boxShadow: '0 6px 20px rgba(26,26,26,.14)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 3,
                  background: colorDe(detalle.sector.id),
                }}
              />
              <span style={{ fontFamily: mono, fontSize: 13.5, fontWeight: 600, color: c.texto }}>
                {detalle.posicion.codigo}
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => setElegido(null)}
                aria-label="Cerrar"
                style={{
                  height: 26,
                  width: 26,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 7,
                  border: `1px solid ${c.borde}`,
                  background: c.panel,
                  cursor: 'pointer',
                }}
              >
                <Icono nombre="close" size={15} color={c.texto3} />
              </button>
            </div>

            <div style={{ fontSize: 12, color: c.texto2, lineHeight: 1.5, marginBottom: 9 }}>
              {detalle.sector.nombre} · {detalle.sector.uso}
              <br />
              Rack <span style={{ fontFamily: mono }}>{detalle.rack.id}</span> · {detalle.rack.columnas} posiciones ·{' '}
              {detalle.rack.niveles} niveles
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* La zona de piso no tiene niveles: se listan todos sus pallets. */}
              {(detalle.posicion.zona?.map((z) => ({ ...z, nivel: null, ocupado: true })) ?? detalle.posicion.niveles).map((n) => (
                <div
                  key={n.pallet ?? n.nivel}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 8,
                    background: n.ocupado ? c.verdeTinte : c.fondo,
                    border: `1px solid ${n.ocupado ? c.verdeBorde : c.borde}`,
                  }}
                >
                  <span style={{ fontFamily: mono, fontSize: 11.5, fontWeight: 600, color: c.texto2 }}>{n.nivel === null ? 'Z' : `N${n.nivel}`}</span>
                  {n.ocupado ? (
                    <span style={{ fontSize: 11.5, color: c.texto2, lineHeight: 1.4, minWidth: 0 }}>
                      <span style={{ fontFamily: mono, color: c.texto }}>{n.pallet}</span>
                      <br />
                      {n.articulo} · {n.tipo === 'completo' ? 'entero' : `${n.piezas} pz`}
                      {n.dias !== null && ` · ${n.dias} días`}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11.5, color: c.texto4 }}>libre</span>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                type="button"
                onClick={() => centrarRack(detalle.rack)}
                title={`Ver el rack ${detalle.rack.id} de frente`}
                style={{
                  ...botonSecundario,
                  flex: 1,
                  minHeight: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  background: aislado === detalle.rack.id ? c.verdeSuave : c.panel,
                  borderColor: aislado === detalle.rack.id ? c.verde : c.bordeFuerte,
                  color: aislado === detalle.rack.id ? c.verde : c.texto2,
                }}
              >
                <Icono
                  nombre="center_focus_strong"
                  size={16}
                  color={aislado === detalle.rack.id ? c.verde : c.texto3}
                />
                Centrar
              </button>
              <button
                type="button"
                onClick={() => onPosicion(detalle.posicion.codigo)}
                style={{ ...botonSecundario, flex: 1, minHeight: 36 }}
              >
                Abrir
              </button>
            </div>
          </motion.div>
        )}
        </AnimatePresence>

        {/* -------------------------------------------- leyenda, sobre el piso */}
        {!esCelular && (
          <div
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
              maxWidth: 'calc(100% - 24px)',
              padding: '7px 11px',
              borderRadius: 999,
              background: 'rgba(255,255,255,.92)',
              border: `1px solid ${c.borde}`,
              fontSize: 11.5,
              color: c.texto2,
              pointerEvents: 'none',
            }}
          >
            {[
              { t: 'Libre', bg: 'rgba(255,255,255,.45)', bd: c.bordeFuerte },
              { t: 'Con pallet', bg: c.verde, bd: c.verde },
              { t: 'Coincide', bg: c.ambarSuave, bd: c.ambar },
            ].map((l) => (
              <span key={l.t} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 13, height: 9, borderRadius: 2, background: l.bg, border: `1px solid ${l.bd}` }} />
                {l.t}
              </span>
            ))}
            <span style={{ color: c.texto4 }}>
              arrastrá para mover · rueda para acercar · ruedita apretada para girar
            </span>
          </div>
        )}

      </div>
    </div>
  );
}
